/**
 * `ResponsesTurnBridge.ts` — routes the MAIN agent's turns through the
 * Responses API when the active role's configured model cannot ride the
 * realtime WebSocket.
 *
 * Why it exists (measured against the dev proxy, 2026-08-13): the realtime
 * endpoint accepts only realtime-mode models (`gpt-realtime*`) — a
 * Responses-family reasoning model like gpt-5.6-sol is refused at connect with
 * `invalid_model`, and the realtime models refuse a `reasoning.effort` key
 * outright. So "the workflow-builder role thinks on gpt-5.6-sol at medium
 * effort" is only reachable over HTTP `/v1/responses`. While a bridged role is
 * active, the realtime session stays connected on the stock model as the VOICE
 * surface: server VAD keeps transcribing but no longer auto-answers
 * (`create_response: false`), user turns run here instead, and the reply is
 * spoken through the base prompt's `INTERNAL SAY:` contract — the same
 * mechanism workflow script playback already uses.
 *
 * This is NOT a subagent (the retired Workflow Builder reviewer was): it has
 * no prompts, no tool list, and no conversation surface of its own. The
 * orchestrator hands it the main agent's instructions, the role-gated tool
 * schemas, and the shared executor (`triggeredBy: "AI"`, so guardrails,
 * telemetry, and Spark-gaze routing all behave exactly as for a realtime
 * turn), and its activity is re-published through the session host's
 * `notifyExternal*` seam so the panel's thinking ring and tool cards cannot
 * tell the transports apart. Owned by `AIOrchestratorModel` like
 * `RealtimeSession`; deliberately not IOC-registered.
 *
 * Two things this transport can do that the realtime one cannot, both surfaced
 * through that same seam:
 *
 *  - **Reasoning summaries.** Rounds are STREAMED (`stream: true` + SSE) and
 *    `reasoning.summary: "auto"` is requested, so the model's thinking reaches
 *    the panel while it is still thinking rather than alongside the answer.
 *  - **File inputs.** A picked file rides the API's own `input_file` /
 *    `input_image` parts (see `responsesFileInput.ts`) instead of being pasted
 *    into the turn as text, which is all the realtime protocol can express.
 */

import {
  extractFunctionCalls,
  extractMessageOnlyText,
  extractOutputItems,
  streamResponsesAsync,
  tryParseResponsesUsage,
  type ResponsesFunctionCall,
} from "@ai/subagents/aiResponsesUtility";
import type { UsageDelta } from "@ai/observability/AISessionUsageTracker";
import type { IRealtimeConversationLog } from "@ai/observability/RealtimeConversationLog";
import { toolHandedOverSpokenTurn } from "@ai/executor/toolTurnHandover";
import type { ResponsesAttachment } from "./responsesFileInput";
import type { RealtimeSessionConnectionConfig } from "./RealtimeSession";

/** Ceiling on tool-call rounds per user turn (the retired reviewer used 6). */
const K_MAX_TOOL_ROUNDS = 8;

/**
 * Request timeout per Responses call. Twice the reviewer's 60 s: medium
 * reasoning effort legitimately thinks longer than the reviewer's `low`.
 */
const K_REQUEST_TIMEOUT_MS = 120_000;

/**
 * History ceiling (input items). Trimming re-anchors on a user message so a
 * decapitated `function_call_output` or reasoning item can never lead the
 * input (the API refuses orphans when `store: false` echoes items back).
 */
const K_MAX_HISTORY_ITEMS = 48;

export interface ResponsesTurnBridgeDeps {
  /** Full main-agent instructions (role persona + project + workflow context). Rebuilt per round. */
  readonly buildInstructions: () => string;
  /** Per-turn `User Context:` payload, or null (Unity `BuildTurnContext`). */
  readonly buildTurnContext: () => string | null;
  /** Role-gated tool schemas — the SAME set the realtime session would carry. */
  readonly buildToolSchemas: () => ReadonlyArray<Record<string, unknown>>;
  /** Model + effort for the active role (`roleModels` in the shared settings). */
  readonly resolveModel: () => RealtimeSessionConnectionConfig;
  /** Usage-row source label, e.g. the active role's display name. */
  readonly getUsageSource: () => string;
  readonly recordUsage: (source: string, delta: UsageDelta) => void;
  /** Runs one tool through the shared executor pipeline (`triggeredBy: "AI"`). */
  readonly executeToolAsync: (name: string, args: Record<string, unknown>) => Promise<string>;
  /** Speaks + displays the reply (INTERNAL SAY through the realtime session). */
  readonly deliverReply: (text: string) => void;
  /** Panel presentation re-publishers (the session host's `notifyExternal*`). */
  readonly setThinking: (thinking: boolean) => void;
  readonly onToolStarted: (toolName: string) => void;
  readonly onToolCompleted: (toolName: string, result: string) => void;
  /** First reasoning-summary token of the turn — the panel opens its block here. */
  readonly onReasoningStarted: () => void;
  /** Reasoning-summary text, streamed. */
  readonly onReasoningDelta: (delta: string) => void;
  /** Turn over: `durationMs` is what the panel's "Thought for …" label reports. */
  readonly onReasoningCompleted: (durationMs: number) => void;
  readonly reportError: (code: string) => void;
  readonly log: IRealtimeConversationLog;
}

export class ResponsesTurnBridge {
  private m_history: unknown[] = [];
  private m_turnAbort: AbortController | null = null;
  private m_turnGeneration = 0;
  /**
   * Bumped by {@link reset} and by nothing else: which HISTORY a turn belongs
   * to, as opposed to {@link m_turnGeneration}, which says whether it is still
   * the turn being answered. A superseded turn's history carries on (the next
   * turn is appended to it); a reset one's is gone, and a turn that wrote into
   * the replacement would leave tool outputs with no calls in it.
   */
  private m_historyEpoch = 0;
  /**
   * Whether to ask for reasoning summaries. Turned OFF for the app session the
   * first time the endpoint refuses the `summary` key — some org/model
   * combinations do — so the turn is retried without it rather than the role
   * simply not answering. See {@link isReasoningSummaryRejection}.
   */
  private m_reasoningSummarySupported = true;
  /** Wall-clock of the turn's first reasoning token, or null before it. */
  private m_reasoningStartedAt: number | null = null;
  /** Whether any reasoning text has been emitted for the CURRENT turn. */
  private m_reasoningHasText = false;

  constructor(private readonly m_deps: ResponsesTurnBridgeDeps) {}

  /**
   * Runs one user turn. A turn arriving while one is in flight supersedes it
   * (abort + fresh generation), mirroring how a realtime user turn interrupts
   * the active response.
   *
   * `attachments` are Responses content parts built by
   * `ResponsesFileInputModel` — an `input_file` / `input_image` referencing an
   * uploaded object. They lead the user message, so the model reads the file
   * before the instruction about it, and they stay in `m_history` so later
   * turns can still refer back to the document.
   */
  async runUserTurnAsync(
    userText: string,
    attachments: ReadonlyArray<ResponsesAttachment> = [],
  ): Promise<void> {
    const text = userText?.trim() ?? "";
    if (!text && attachments.length === 0) return;

    this.m_turnAbort?.abort();
    // Close the SUPERSEDED turn's thinking block here, before the generation
    // bump: its own `finally` will see a stale generation and decline to, which
    // would leave that block stuck on "Thinking…" while this turn opens a
    // second one below it.
    this.settleReasoning();
    const abort = new AbortController();
    this.m_turnAbort = abort;
    const generation = ++this.m_turnGeneration;
    const historyEpoch = this.m_historyEpoch;
    const isCurrent = () => generation === this.m_turnGeneration && !abort.signal.aborted;

    const config = this.m_deps.resolveModel();
    this.m_deps.log.note("bridge.turn", `model=${config.model}, effort=${config.reasoningEffort ?? "-"}`);

    // The per-turn context rides as its own user item, like the realtime
    // session's `trySendTurnContext`.
    const turnContext = this.m_deps.buildTurnContext();
    if (turnContext) this.m_history.push(userMessage(`User Context:\n${turnContext}`));
    // An attachment picked with nothing typed still needs a text part, or the
    // turn reads as a bare document with no request attached to it.
    const prompt =
      text ||
      `Attached ${attachments.map((a) => `"${a.fileName}"`).join(", ")}.`;
    this.m_history.push(userMessage(prompt, attachments));
    if (attachments.length > 0) {
      this.m_deps.log.note(
        "bridge.attachment",
        attachments.map((a) => `${a.kind}:${a.fileName}`).join(", "),
      );
    }

    this.m_reasoningStartedAt = null;
    this.m_reasoningHasText = false;
    this.m_deps.setThinking(true);
    try {
      for (let round = 0; round < K_MAX_TOOL_ROUNDS; round++) {
        if (!isCurrent()) return;

        const root = await this.requestRoundAsync(config, abort.signal, isCurrent);
        if (!isCurrent()) return;

        const usage = tryParseResponsesUsage(root, config.model);
        if (usage) this.m_deps.recordUsage(this.m_deps.getUsageSource(), usage);

        const functionCalls = extractFunctionCalls(root);
        if (functionCalls.length === 0) {
          const reply = extractMessageOnlyText(root)?.trim() ?? "";
          this.m_history.push(...extractOutputItems(root));
          this.trimHistory();
          if (reply) this.m_deps.deliverReply(reply);
          return;
        }

        this.m_history.push(...extractOutputItems(root));
        const { outputs, endedTurn } = await this.executeToolCallsAsync(functionCalls, isCurrent);
        // A tool can RESET the bridge from inside its own call — a role switch
        // does (`switch_role`, and playback taking the role over when
        // `start_workflow` / `stop_workflow` lands). The calls these outputs
        // answer went with the old history, so writing the outputs into the
        // fresh one leaves an output with no call, which the endpoint refuses on
        // the next bridged round. A supersede by a new user turn does NOT reset,
        // and its history still needs them — hence the epoch, not `isCurrent`.
        if (historyEpoch !== this.m_historyEpoch) return;
        this.m_history.push(...outputs);
        this.trimHistory();
        if (endedTurn) return; // present_options / a started run: something else has the floor.
      }
      console.warn("[ResponsesTurnBridge] turn hit the tool-round ceiling");
      this.m_deps.log.note("bridge.turn", "tool-round ceiling reached");
    } catch (e) {
      if (abort.signal.aborted) return;
      const message = e instanceof Error ? e.message : String(e);
      console.warn("[ResponsesTurnBridge] turn failed:", message);
      this.m_deps.log.note("bridge.error", message);
      if (isCurrent()) this.m_deps.reportError("text_bridge_error");
    } finally {
      // Only the CURRENT turn may release the thinking state — a superseded
      // turn unwinding must not clear the indicator its successor owns.
      if (generation === this.m_turnGeneration) {
        this.m_deps.setThinking(false);
        // Settle the panel's thinking block on EVERY exit path, including an
        // error or the tool-round ceiling: a block left open would sit on its
        // caret forever and the next turn's reasoning would append to it.
        this.settleReasoning();
      }
    }
  }

  /**
   * One round of the tool loop.
   *
   * Streamed (`streamResponsesAsync`) so reasoning summaries reach the panel
   * while the model is still thinking; the returned root is the same shape the
   * buffered call gave back, so everything downstream is unchanged.
   */
  private async requestRoundAsync(
    config: RealtimeSessionConnectionConfig,
    signal: AbortSignal,
    isCurrent: () => boolean,
  ): Promise<Record<string, unknown>> {
    const body = (withSummary: boolean): Record<string, unknown> => ({
      model: config.model,
      ...(config.reasoningEffort !== null
        ? {
            reasoning: {
              effort: config.reasoningEffort,
              // "auto" is what produces the summary the panel renders; null is
              // the API's "don't summarize".
              summary: withSummary ? "auto" : null,
            },
          }
        : {}),
      input: this.m_history,
      // Rebuilt every round so a mid-loop `update_workflow` is visible
      // next round (the retired reviewer's chat mode did the same).
      instructions: this.m_deps.buildInstructions(),
      tools: [...this.m_deps.buildToolSchemas()],
      tool_choice: "auto",
      text: { format: { type: "text" } },
      store: false,
      // With store:false the tool loop echoes output items back as
      // input; reasoning items need their encrypted payload to
      // round-trip.
      include: ["reasoning.encrypted_content"],
    });

    const handlers = {
      onReasoningPartAdded: () => {
        if (!isCurrent()) return;
        // Successive summary parts are separate paragraphs; the break is
        // emitted lazily so a block never opens on blank lines.
        if (this.m_reasoningHasText) this.emitReasoning("\n\n");
      },
      onReasoningDelta: (delta: string) => {
        if (isCurrent()) this.emitReasoning(delta);
      },
    };

    try {
      return await streamResponsesAsync(
        body(this.m_reasoningSummarySupported),
        signal,
        K_REQUEST_TIMEOUT_MS,
        handlers,
      );
    } catch (e) {
      if (!this.m_reasoningSummarySupported || signal.aborted) throw e;
      if (!isReasoningSummaryRejection(e)) throw e;
      // The endpoint refuses summaries for this model/org — stop asking for
      // the rest of the app session and answer the round anyway. Losing the
      // thinking block is a far smaller failure than losing the turn.
      this.m_reasoningSummarySupported = false;
      console.warn("[ResponsesTurnBridge] reasoning summaries unavailable; retrying without");
      this.m_deps.log.note("bridge.reasoning", "summary unsupported — disabled");
      return await streamResponsesAsync(body(false), signal, K_REQUEST_TIMEOUT_MS, handlers);
    }
  }

  /** First token opens the panel's block and starts the "Thought for …" clock. */
  private emitReasoning(delta: string): void {
    if (!delta) return;
    if (this.m_reasoningStartedAt === null) {
      this.m_reasoningStartedAt = performance.now();
      this.m_deps.onReasoningStarted();
    }
    this.m_reasoningHasText = true;
    this.m_deps.onReasoningDelta(delta);
  }

  /**
   * Closes the turn's thinking block. The reported duration is wall-clock from
   * the first reasoning token to the end of the turn — tool rounds included,
   * because from the user's side that whole span is the model working before
   * it answered.
   */
  private settleReasoning(): void {
    if (this.m_reasoningStartedAt === null) return;
    const elapsed = Math.max(0, performance.now() - this.m_reasoningStartedAt);
    this.m_reasoningStartedAt = null;
    this.m_reasoningHasText = false;
    this.m_deps.onReasoningCompleted(elapsed);
  }

  /** Force-stop / supersede hook (the realtime `response.cancel` analogue). */
  cancelActiveTurn(): void {
    this.m_turnAbort?.abort();
    this.m_turnAbort = null;
    this.m_deps.setThinking(false);
    // Settle here rather than waiting for the aborted turn's `finally`: it
    // resumes a tick later, and `reset()` bumps the generation in between,
    // which is exactly the path that would strand an open block on screen.
    this.settleReasoning();
  }

  /**
   * Adds a line the model reads on its NEXT turn and never answers — the
   * bridge's half of `RealtimeSession.sendContextNote`. Nothing runs now: a
   * note is context, not a turn, so it must not start a round or supersede
   * one in flight. It stays in the history like the `User Context:` items do.
   */
  appendContextNote(text: string): void {
    this.m_history.push(userMessage(text));
    this.trimHistory();
  }

  /** Drops the conversation (role exit, project switch, reset_conversation). */
  reset(): void {
    this.cancelActiveTurn();
    this.m_history = [];
    this.m_historyEpoch++;
    this.m_turnGeneration++;
  }

  private async executeToolCallsAsync(
    calls: ResponsesFunctionCall[],
    isCurrent: () => boolean,
  ): Promise<{ outputs: unknown[]; endedTurn: boolean }> {
    const outputs: unknown[] = [];
    let endedTurn = false;
    for (const call of calls) {
      if (!isCurrent()) break;
      this.m_deps.onToolStarted(call.name);
      this.m_deps.log.logTool(call.name, "call", safeParseArgs(call.argumentsJson));
      let result: string;
      try {
        result = await this.m_deps.executeToolAsync(call.name, safeParseArgs(call.argumentsJson));
      } catch (e) {
        result = `error: ${e instanceof Error ? e.message : String(e)}`;
      }
      this.m_deps.onToolCompleted(call.name, result);
      this.m_deps.log.logTool(call.name, "result", result);
      outputs.push({ type: "function_call_output", call_id: call.callId, output: result || "ok" });
      // Some tools hand the SPOKEN turn away — see `toolTurnHandover.ts`. The
      // realtime path suppresses its follow-up response on the same rule.
      if (toolHandedOverSpokenTurn(call.name, result)) {
        endedTurn = true;
      }
    }
    return { outputs, endedTurn };
  }

  private trimHistory(): void {
    if (this.m_history.length <= K_MAX_HISTORY_ITEMS) return;
    let trimmed = this.m_history.slice(-K_MAX_HISTORY_ITEMS);
    // Re-anchor on a user message so no orphan function_call_output / reasoning
    // item leads the input.
    const firstUser = trimmed.findIndex(
      (item) => (item as { role?: string } | null)?.role === "user",
    );
    if (firstUser > 0) trimmed = trimmed.slice(firstUser);
    this.m_history = trimmed;
  }
}

/**
 * A user input item. File parts LEAD the content array so the model reads the
 * document before the sentence that asks about it.
 */
function userMessage(
  text: string,
  attachments: ReadonlyArray<ResponsesAttachment> = [],
): Record<string, unknown> {
  return {
    role: "user",
    content: [...attachments.map((a) => a.part), { type: "input_text", text }],
  };
}

/**
 * Whether a failed round was refused specifically because of the `summary` key
 * — the one rejection worth retrying without, since dropping the thinking
 * block costs far less than dropping the turn. Matched on the endpoint's own
 * message rather than the status alone, so an unrelated 400 still surfaces.
 */
function isReasoningSummaryRejection(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  if (!message.includes("summary")) return false;
  return (
    message.includes("http 400") ||
    message.includes("unsupported") ||
    message.includes("invalid") ||
    message.includes("not supported") ||
    message.includes("must be verified")
  );
}

function safeParseArgs(json: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(json?.trim() || "{}");
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}
